import { useEffect, useRef, useState } from 'react';
import { ConnectionQuality, RoomEvent, type Room } from 'livekit-client';

/** How long quality must stay poor before we drop video, and recover before restoring. */
const DEGRADE_AFTER_MS = 6_000;
const RECOVER_AFTER_MS = 12_000;

export interface LowBandwidthState {
  /** True while we have taken video down on the user's behalf. */
  videoPausedForBandwidth: boolean;
  quality: ConnectionQuality;
}

/**
 * Audio-first policy.
 *
 * When the uplink degrades, LiveKit already drops to a lower simulcast layer on
 * its own. If it stays poor past that, the only thing left that helps is to
 * stop sending video entirely so the remaining bandwidth goes to speech.
 *
 * Both directions are deliberately slow and asymmetric: a brief lift blip
 * should not flap the camera off and on, and recovery waits twice as long as
 * degradation so the call settles instead of oscillating.
 *
 * If the user has manually turned their camera off, this stays out of the way,
 * and it never re-enables a camera the user chose to disable.
 */
export function useLowBandwidthGuard(
  room: Room | undefined,
  enabled: boolean,
): LowBandwidthState {
  const [quality, setQuality] = useState<ConnectionQuality>(ConnectionQuality.Unknown);
  const [videoPausedForBandwidth, setPaused] = useState(false);
  const poorSince = useRef<number | null>(null);
  const goodSince = useRef<number | null>(null);
  const pausedByUs = useRef(false);

  useEffect(() => {
    if (!room) return;
    const onQuality = (q: ConnectionQuality, participant?: { isLocal?: boolean }) => {
      if (participant && participant.isLocal === false) return;
      setQuality(q);
    };
    room.on(RoomEvent.ConnectionQualityChanged, onQuality);
    return () => {
      room.off(RoomEvent.ConnectionQualityChanged, onQuality);
    };
  }, [room]);

  useEffect(() => {
    if (!room || !enabled) return;

    const timer = setInterval(() => {
      const local = room.localParticipant;
      const now = Date.now();
      const poor =
        local.connectionQuality === ConnectionQuality.Poor ||
        local.connectionQuality === ConnectionQuality.Lost;

      if (poor) {
        goodSince.current = null;
        poorSince.current ??= now;

        const sustained = now - (poorSince.current ?? now) >= DEGRADE_AFTER_MS;
        if (sustained && local.isCameraEnabled && !pausedByUs.current) {
          pausedByUs.current = true;
          setPaused(true);
          void local.setCameraEnabled(false).catch(() => undefined);
        }
        return;
      }

      poorSince.current = null;
      goodSince.current ??= now;

      const recovered = now - (goodSince.current ?? now) >= RECOVER_AFTER_MS;
      if (recovered && pausedByUs.current) {
        pausedByUs.current = false;
        setPaused(false);
        void local.setCameraEnabled(true).catch(() => undefined);
      }
    }, 2_000);

    return () => clearInterval(timer);
  }, [room, enabled]);

  return { videoPausedForBandwidth, quality };
}
